import type { SupportRequestDto, SupportRequestPatchDto } from './dto';
import type { ReplyEntry, RequestPriority, RequestStatus, SupportRequest } from './types';

/** Бэкенд присылает "None" и пустые строки — считаем их отсутствием значения. */
function str(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === 'None' || trimmed === 'null') return null;
    return trimmed;
}

/** created_at приходит в RFC 1123 ("Sat, 11 Jul 2026 05:27:00 GMT") — new Date его разбирает. */
function toDate(value: string | null | undefined): Date | null {
    const raw = str(value);
    if (raw === null) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Статус приходит либо ключом (accepted / in_queue / completed), либо русской
 * подписью: детальная ручка отдаёт именно её — это видно по старому DetailedRequest.
 */
const STATUS_FROM_DTO: Record<string, RequestStatus> = {
    accepted: 'new',
    'принято к сведению': 'new',
    in_queue: 'work',
    in_progress: 'work',
    'в очереди на обработку': 'work',
    'в работе': 'work',
    completed: 'done',
    исполнено: 'done',
    завершено: 'done',
    rejected: 'rej',
    declined: 'rej',
    отклонено: 'rej',
};

const STATUS_TO_DTO: Record<RequestStatus, string> = {
    new: 'accepted',
    work: 'in_queue',
    done: 'completed',
    rej: 'rejected',
};

const PRIORITY_FROM_DTO: Record<string, RequestPriority> = {
    low: 'low',
    низкий: 'low',
    medium: 'mid',
    mid: 'mid',
    средний: 'mid',
    high: 'high',
    высокий: 'high',
};

const PRIORITY_TO_DTO: Record<RequestPriority, string> = {
    low: 'low',
    mid: 'medium',
    high: 'high',
};

export function toStatus(value: string | null): RequestStatus {
    const key = str(value)?.toLowerCase();
    return (key !== undefined && STATUS_FROM_DTO[key]) || 'new';
}

export function toPriority(value: string | null): RequestPriority {
    const key = str(value)?.toLowerCase();
    return (key !== undefined && PRIORITY_FROM_DTO[key]) || 'low';
}

export function toSupportRequest(dto: SupportRequestDto): SupportRequest {
    const createdAt = toDate(dto.created_at) ?? new Date();
    const answer = str(dto.answer);
    const replies: ReplyEntry[] = [];

    if (answer !== null) {
        replies.push({
            id: `${dto.id}:answer`,
            author: 'Техническая поддержка',
            date: toDate(dto.answer_time) ?? createdAt,
            text: answer,
        });
    }

    return {
        id: dto.id,
        createdAt,
        status: toStatus(dto.status),
        priority: toPriority(dto.criticality),
        theme: str(dto.problem) ?? 'Другое',
        text: str(dto.description) ?? '',
        org: str(dto.organization) ?? '',
        inn: '', // отдельного поля ИНН в выдаче нет, он встречается только внутри текста обращения
        email: str(dto.email) ?? '',
        replies,
        files: dto.files ?? {},
        deadline: toDate(dto.deadline),
    };
}

export interface SupportRequestPatch {
    status?: RequestStatus;
    priority?: RequestPriority;
    answer?: string;
    briefSolution?: string;
    comment?: string;
}

export function toSupportRequestPatchDto(patch: SupportRequestPatch): SupportRequestPatchDto {
    const dto: SupportRequestPatchDto = {};
    if (patch.status !== undefined) dto.status = STATUS_TO_DTO[patch.status];
    if (patch.priority !== undefined) dto.criticality = PRIORITY_TO_DTO[patch.priority];
    if (patch.answer !== undefined) dto.answer = patch.answer;
    if (patch.briefSolution !== undefined) dto.brief_solution = patch.briefSolution;
    if (patch.comment !== undefined) dto.comment = patch.comment;
    return dto;
}